import { parseBrowseCategoryPath, parseLegacyCategoryPath } from '@/utils/browsePath/browsePath';
import { getTopCategoryIdFromSlug } from '@/utils/browsePath/resolveCategorySlug';
import { getFilterTermFromQueryParams, isArchivedSearch, parseQueryParams } from '@/utils/queryParams';
import { getCombinationCategoryIdsFromPath, isSimilarItemsPage } from '@/utils/urls';

import { CategoryRouteParams, RelatedSearchTilesParams } from './types';

type BuildRouteContextArgs = {
    location: { pathname: string; search?: string };
    params: CategoryRouteParams;
};

export type CategoryRouteContext = {
    archivedSearch: boolean;
    basePathname: string;
    browseParsed: ReturnType<typeof parseBrowseCategoryPath>;
    browseSplat: string | undefined;
    hasRegularLegacyCategory: boolean;
    isBrowseSlugRoute: boolean;
    isCombinationCategory: boolean;
    itemId: number;
    keywords: string;
    legacyCategoryId: number;
    legacyCategoryId2: number;
    legacyPathParsed: ReturnType<typeof parseLegacyCategoryPath>;
    locationSearch: string;
    pageName: 'price-guide' | 'category';
    params: CategoryRouteParams;
    pathname: string;
    queryParams: Record<string, any>;
    relatedSearchTilesParams: RelatedSearchTilesParams;
};

const getBrowseSplat = (params: CategoryRouteParams): string | undefined => {
    const splat = params['*'];
    return typeof splat === 'string' ? splat : undefined;
};

export const normalizePathname = (pathname: string): string => {
    if (pathname.length > 1 && pathname.endsWith('/')) {
        return pathname;
    }
    return pathname.endsWith('/') ? pathname : `${pathname}/`;
};

const getRelatedSearchTilesParams = ({
    hasRegularLegacyCategory,
    isBrowseSlugRoute,
    isCombinationCategory,
    legacyCategoryId,
    legacyPathParsed,
    params,
    queryParams,
}: {
    hasRegularLegacyCategory: boolean;
    isBrowseSlugRoute: boolean;
    isCombinationCategory: boolean;
    legacyCategoryId: number;
    legacyPathParsed: ReturnType<typeof parseLegacyCategoryPath>;
    params: CategoryRouteParams;
    queryParams: Record<string, any>;
}): RelatedSearchTilesParams => {
    if (isCombinationCategory) {
        return {
            categoryIds: [],
            concatenatedFacetIds: [getCombinationCategoryIdsFromPath(params, queryParams)],
        };
    }

    if (legacyPathParsed) {
        return {
            categoryIds: [Number(legacyPathParsed.numericCategoryId)],
            concatenatedFacetIds: undefined,
        };
    }

    if (hasRegularLegacyCategory) {
        return {
            categoryIds: [legacyCategoryId],
            concatenatedFacetIds: undefined,
        };
    }

    if (isBrowseSlugRoute && params.browseCategorySlug) {
        const topId = getTopCategoryIdFromSlug(params.browseCategorySlug);
        return {
            categoryIds: topId ? [topId] : [],
            concatenatedFacetIds: undefined,
        };
    }

    return {
        categoryIds: [],
        concatenatedFacetIds: undefined,
    };
};

export const buildRouteContext = ({ location, params }: BuildRouteContextArgs): CategoryRouteContext => {
    const queryParams = parseQueryParams(location.search);
    const { pathname: rawPathname, search: locationSearch = '' } = location;
    const pathname = normalizePathname(rawPathname);
    const legacyPathParsed = parseLegacyCategoryPath(pathname);
    const browseParsed = parseBrowseCategoryPath(pathname);
    const browseSplat = getBrowseSplat(params);
    const isBrowseSlugRoute = Boolean(params.browseCategorySlug);
    const legacyCategoryId = params.categoryId ? Number(params.categoryId) : NaN;
    const legacyCategoryId2 = params.categoryId2 ? Number(params.categoryId2) : NaN;
    const isCombinationCategory = Boolean(params.categoryId2 && !Number.isNaN(legacyCategoryId2));
    const hasRegularLegacyCategory = Boolean(params.categoryId && !Number.isNaN(legacyCategoryId));
    const itemId = Number(queryParams?.itemId);
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const pageName = archivedSearch ? 'price-guide' : 'category';
    const keywords = String(getFilterTermFromQueryParams(queryParams) ?? '');

    return {
        archivedSearch,
        basePathname: rawPathname,
        browseParsed,
        browseSplat,
        hasRegularLegacyCategory,
        isBrowseSlugRoute,
        isCombinationCategory,
        itemId,
        keywords,
        legacyCategoryId,
        legacyCategoryId2,
        legacyPathParsed,
        locationSearch,
        pageName,
        params,
        pathname,
        queryParams,
        relatedSearchTilesParams: getRelatedSearchTilesParams({
            hasRegularLegacyCategory,
            isBrowseSlugRoute,
            isCombinationCategory,
            legacyCategoryId,
            legacyPathParsed,
            params,
            queryParams,
        }),
    };
};

export const isSimilarItemsRoute = (pathname: string) => isSimilarItemsPage(pathname);
